// Importaciones de interfaces.
import { IProduct } from '../interfaces/product.interface';

/**
 * Representa un producto en el sistema, implementando la interfaz `IProduct`.
 *
 * Esta clase proporciona una estructura para almacenar la información de un producto,
 * como su identificador, nombre, descripción, imagen, estado, y las fechas de
 * creación y actualización. Además, ofrece un constructor para inicializar estos
 * valores de manera flexible.
 */
export class Product implements IProduct {
  /**
   * El identificador único del producto.
   * Puede ser `null` si el producto aún no ha sido asignado un ID.
   */
  id: number | null;

  /**
   * El nombre del producto.
   * Puede ser `null` si el producto no tiene nombre asignado.
   */
  name: string | null;

  /**
   * La descripción del producto.
   * Puede ser `null` si no se proporciona una descripción.
   */
  description: string | null;

  /**
   * La URL de la imagen del producto.
   * Puede ser `null` si no se proporciona una imagen.
   */
  image: string | null;

  /**
   * Archivo de imagen asociado al producto.
   *
   * Esta propiedad almacena el archivo de imagen del producto.
   * Puede ser un archivo de tipo `File` o `null` si no se ha seleccionado ninguna imagen.
   */
  imageFile: File | null;

  /**
   * El estado del producto, indicando si está habilitado o deshabilitado.
   * Puede ser `null` si no se especifica el estado.
   */
  disabled: number | null;

  /**
   * La fecha de la última actualización del producto.
   * Puede ser `null` si el producto aún no ha sido actualizado.
   */
  updatedAt: string | null;

  /**
   * La fecha de creación del producto.
   * Puede ser `null` si el producto aún no ha sido creado o registrado.
   */
  createdAt: string | null;

  /**
   * Constructor de la clase que inicializa una instancia de `Product`.
   *
   * @param {Partial<IProduct>} product - Un objeto parcial de `IProduct` que contiene las siguientes propiedades opcionales:
   *   - `id` (opcional): Identificador del producto. Por defecto, `null`.
   *   - `name` (opcional): Nombre del producto. Por defecto, `null`.
   *   - `description` (opcional): Descripción del producto. Por defecto, `null`.
   *   - `image` (opcional): URL de la imagen del producto. Por defecto, `null`.
   *   - `disabled` (opcional): Estado del producto (habilitado o deshabilitado). Por defecto, `null`.
   *   - `updatedAt` (opcional): Fecha de última actualización. Por defecto, `null`.
   *   - `createdAt` (opcional): Fecha de creación. Por defecto, `null`.
   */
  constructor({
    id = null,
    name = null,
    description = null,
    image = null,
    imageFile = null,
    disabled = null,
    updatedAt = null,
    createdAt = null,
  }: Partial<IProduct> = {}) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.image = image;
    this.imageFile = imageFile;
    this.disabled = disabled;
    this.updatedAt = updatedAt;
    this.createdAt = createdAt;
  }
}
